import { IDtvTextProps } from "@directv-digital/dds.ui.dtv-text";
import { LocalRsnApiResponse } from "@directv-digital/dds.apis.local-rsn-chnls";
import { County } from "@directv-digital/dds.apis.county-lookup";

export interface ZipcodeModuleProps {
  // Required
  id: string;
  mode: "light" | "dark";
  /** Main difference is "small" on desktop has 48px height, not 56px */
  size?: "small" | "medium";
  /** Horizontal alignment of the module inside its containing box */
  position?: "left" | "center" | "right";
  /** Callback sent to parent when the search button is clicked */
  onSearchButtonClick?: (zipcode: string, counties: County[] | null, rsnFee?: string, localRsnChnls?: LocalRsnApiResponse | null, county?: County) => void;
  /** Callback sent to parent when either
   * (a) Update button is clicked and RSN Fee prefetch is completed, or
   * (b) RSN Fee fetch completed (after the search button is clicked) */
  onRsnFeeConfirmed?: (fee?: string) => void;
  /** Opt-in: re-decide the RSN fee PER ZIP. Off by default, and default-off is
   * load-bearing — it is what keeps every existing consumer on the behaviour it
   * ships with today. Two things turn on together, because they are one contract:
   *
   *  - DELIVERY. Under `startEmpty` the fee is normally suppressed outright, which
   *    is permanent rather than "until the first search", so the refreshed fee that
   *    lands in context a tick AFTER a submit never arrives. Opting in opens that
   *    gate once a ZIP has been submitted, and closes it again on reset.
   *  - RETRACTION. A ZIP that resolves no fee calls `onRsnFeeConfirmed(undefined)`
   *    instead of silently leaving the previous ZIP's figure standing.
   *
   * A caller that opts in must therefore read `undefined` as a deliberate
   * retraction rather than as a fault. That is why this is not folded into
   * `startEmpty`: `startEmpty` means "do not auto-fill the ZIP" and other
   * consumers pass it, including the v1 agent tool, whose handler logs a defect
   * for anything it does not read as a fee. (WAT-11842: a fee belongs to the ZIP
   * it was resolved for.) */
  perZipRsnFee?: boolean;
  /** Callback sent to parent when the RSN Fee API gives bad response */
  onRsnFeeApiError?: (error?: string) => void;
  /** Callback sent to parent when either
   * (a) Update button is clicked and RSN Fee prefetch is completed, or
   * (b) RSN Fee fetch completed (after the search button is clicked) */
  // `meta.failed` is set when the lookup failed; `channels` is then empty lists
  // so destructuring never throws (WAT-12673). When it is set, a caller must
  // NOT treat the lists as "this market has no locals/RSNs": keep any error
  // state (skip setApiError(false)), skip eligibility/persistence writes
  // (sessionStorage isLocalsEligible, handleUpdateLocalsEligible) and keep
  // "not loaded" state as it was. Rendering the empty lists is fine.
  onLocalRsnChnlsConfirmed?: (
    channels: LocalRsnApiResponse | null,
    dma?: string,
    nielsenDma?: string,
    meta?: { failed?: boolean }
  ) => void | null;
  /** Callback sent to parent when Local/Rsn Channels API gives bad response */
  onLocalRsnChnlsApiError?: (error?: string) => void;
  /** Callback sent to parent when either
   * (a) Update button is clicked and RSN Fee prefetch is completed, or
   * (b) RSN Fee fetch completed (after the search button is clicked) */
  onLocalRsnChnlApiLoading?: (isLoading: boolean) => void;
  /** Callback sent to parent when the cache is confirmed with zipcode and county data */
  onCacheConfirmed?: (zipcode: string, counties: County[] | null, rsnFee?: string, localRsnChnls?: LocalRsnApiResponse | null, county?: County) => void;
  /** Callback sent to parent when the Zipcode Lookup (County) API gives bad response */
  onCountyApiError?: (error?: string) => void;
  /** Search for satellite or stream local/rsn channels */
  localRsnApiOptions?: {
    satellite?: boolean;
    stream?: boolean;
  };
  /** !! Provide below if component is a secondary inpage component
   *  - Data should be retrieved from a Zipcode Module higher in DOM tree
   *  - Once enhanced context done, can be removed */
  initData?: {
    zipcode?: string;
    counties?: County[] | null;
    county?: County | null;
    rsnFee?: string;
    localRsnChnls?: LocalRsnApiResponse | null;
  };
  isConditional?: boolean;
  /** Start blank (no app-context/edgescape/cookie auto-fill) with the drawer
   *  open, so the user must enter a ZIP. Agent compare tool only; default false. */
  startEmpty?: boolean;
  /** Bump to reset the ZIP/county fields to their page-load state and reopen the
   *  input. Opt-in like `startEmpty` — unset means unchanged behaviour. */
  resetSignal?: number;
  /** Give the county dropdown a fixed 256px width (Figma node 13987:15915
   *  "Dropdown") instead of the original fluid/remaining-space width. Agent
   *  compare tool only; default false — every other consumer keeps the
   *  original width unchanged. */
  fixedCountyWidth?: boolean;
  /** Optional component name string to differentiate the payload to identify rsn fee for regular & with agent compare tool channel */
  componentType?: string;
  // --- start -- CI Values -------------------
  ciConfig?: {
    /** Bolded header text, before the location pill. Defaults to "Showing details for:" */
    displayText?: string;
    /** [Optional] Tooltip next to display header */
    displayTooltip?: IDtvTextProps["tooltip"]
    /** Placeholder shown inside the location pill before a zip has been submitted.
     *  Defaults to "City, State ZIP Code" */
    locationPillPlaceholder?: string;
    /** Error helper message when search button is clicked with a partial zipcode */
    zipCodeInputErrorMsg?: string;
    /** Error helper message when the County Lookup API returns no results */
    zipCodeLookupErrorMsg?: string;
    /** Error helper message when search button is clicked with no county selection */
    countySelectionErrorMsg?: string;
    /** Helper message when search button is clicked with a partial zipcode */
    countyApiHangingMsg?: string;
    /** Error display text when the Zipcode Lookup API gives bad response */
    countyApiErrorMsg?: string;
    /** Label for button to open the drawer. Defaults to "Change" */
    drawerOpenText?: string;
    /** Label for button to close the drawer. Defaults to "Hide" */
    drawerCloseText?: string;
    /** Label/placeholder for the ZIP Code field. Defaults to "Zip Code" */
    zipCodeLabel?: string;
    /** Label/placeholder for the ZIP Code field. Defaults to "Select a County" */
    countyLabel?: string;
    /** Label for the CTA once a location has already been submitted. Defaults to "Update location" */
    ctaLabel?: string;
    /** Label for the CTA before any location has been submitted. Defaults to "Add location" */
    addLocationCtaLabel?: string;
    /** Position attribute for CTA button analytics. Defaults to "body" */
    ctaLinkPosition?: string;
    /** Aria-label for the CTA button (for analytics). If provided, overrides the computed label. */
    ctaLinkName?: string;
  }
  // -- end -- CI Values
}

export const defaultCiConfig = {
  displayText: "Showing details for:",
  displayTooltip: {},
  locationPillPlaceholder: "City, State ZIP Code",
  zipCodeInputErrorMsg: "Enter a ZIP Code to continue",
  zipCodeLookupErrorMsg: "Having trouble finding that ZIP Code",
  countySelectionErrorMsg: "You must select a county to continue",
  countyApiHangingMsg: "Sorry, this search is taking a while...",
  countyApiErrorMsg: "We are having trouble showing local channel data, Please try again later",
  drawerOpenText: "Change",
  drawerCloseText: "Hide",
  zipCodeLabel: "ZIP Code",
  countyLabel: "Select your county",
  ctaLabel: "Update location",
  addLocationCtaLabel: "Add location",
  ctaLinkPosition: "body",
  ctaLinkName: undefined
}
